/**
 * useCashierAuth Hook
 * Kasiyer giriş/çıkış handler'ları:
 *  - handleOpenCashierLogin: kasiyer giriş modalını açar
 *  - handleCashierLogin: giriş başarılı → yetkileri set eder, bekleyen ürünü ekler/menü açar
 *  - handleCashierLogout: çıkış → yetkileri ve ön ödemeli kart state'ini sıfırlar
 *
 * Not: currentCashier ve isCashierLoginModalOpen state'leri QuickSaleScreen'de kalır
 * (currentCashier handler'lardan önce useQuickSaleProductSearch içinde kullanıldığı için),
 * bu hook bunları param/setter olarak alır.
 */

import { useCallback } from 'react';
import { CashierInfo } from '../components/modals/CashierLoginModal';
import { PersonnelPermissions } from '../components/modals/QuickSaleScreenSettingsModal';
import { LAYOUT } from '../constants';
import type { InventoryItem } from '../types';

export interface UseCashierAuthParams {
  currentCashier: CashierInfo | null;
  setCurrentCashier: React.Dispatch<React.SetStateAction<CashierInfo | null>>;
  setIsCashierLoginModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentUserPermissions: React.Dispatch<React.SetStateAction<PersonnelPermissions | null>>;
  /** screenSettings?.useCashierSystem */
  useCashierSystem: boolean | undefined;
  prepaidSystemEnabled: boolean;
  prepaidCard: any;
  setPrepaidCard: React.Dispatch<React.SetStateAction<any>>;
  setSelectedPrepaidWalletId: React.Dispatch<React.SetStateAction<number | null>>;
  setIsPrepaidCardSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pendingProductForCashierLogin: any;
  setPendingProductForCashierLogin: React.Dispatch<React.SetStateAction<any>>;
  pendingQuantityForCashierLogin: number;
  setPendingQuantityForCashierLogin: React.Dispatch<React.SetStateAction<number>>;
  sqlInventoryItems: InventoryItem[];
  setPendingMenuProduct: React.Dispatch<React.SetStateAction<any>>;
  setIsMenuOptionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addToCart: (product: any, quantityToAdd?: number) => void;
  focusSearchBar: () => void;
}

export interface UseCashierAuthReturn {
  handleOpenCashierLogin: () => void;
  handleCashierLogin: (cashierInfo: CashierInfo) => void;
  handleCashierLogout: () => void;
}

/**
 * Kasiyer giriş/çıkış hook'u
 */
export const useCashierAuth = ({
  currentCashier,
  setCurrentCashier,
  setIsCashierLoginModalOpen,
  setCurrentUserPermissions,
  useCashierSystem,
  prepaidSystemEnabled,
  prepaidCard,
  setPrepaidCard,
  setSelectedPrepaidWalletId,
  setIsPrepaidCardSearchOpen,
  pendingProductForCashierLogin,
  setPendingProductForCashierLogin,
  pendingQuantityForCashierLogin,
  setPendingQuantityForCashierLogin,
  sqlInventoryItems,
  setPendingMenuProduct,
  setIsMenuOptionModalOpen,
  addToCart,
  focusSearchBar,
}: UseCashierAuthParams): UseCashierAuthReturn => {
  /**
   * ✅ Kasiyer giriş modalını aç
   */
  const handleOpenCashierLogin = useCallback(() => {
    setIsCashierLoginModalOpen(true);
  }, [setIsCashierLoginModalOpen]);

  /**
   * ✅ Kasiyer giriş başarılı
   * - Kasiyer giriş yaptığında bekleyen ürün varsa sepete ekle
   * - Menülü ürün ise opsiyon modalı açılır
   */
  const handleCashierLogin = useCallback((cashierInfo: CashierInfo) => {
    console.log('✅ [QuickSaleScreen] Kasiyer giriş yaptı:', cashierInfo.cashierName);
    console.log('🔐 [QuickSaleScreen] Kasiyer yetkileri:', cashierInfo.permissions);
    setCurrentCashier(cashierInfo);
    setIsCashierLoginModalOpen(false);

    // ✅ YENİ: Kasiyer yetkilerini currentUserPermissions state'ine set et
    // Bu sayede ActionControlsPanel ve diğer bileşenler kasiyer yetkilerini kullanabilir
    if (cashierInfo.permissions) {
      const cashierPermissions: PersonnelPermissions = {
        changePrice: cashierInfo.permissions.canChangePrice || false,
        applyDiscount: cashierInfo.permissions.canApplyDiscount || false,
        cancel: cashierInfo.permissions.canCancelSale || false,
        viewReports: cashierInfo.permissions.canViewReports || false,
        warehouseOperations: cashierInfo.permissions.canWarehouseOperations || false,
        viewAccountBalance: cashierInfo.permissions.canViewAccountBalance || false,
        listAccounts: cashierInfo.permissions.canListAccounts || false,
        canQuickAddItem: cashierInfo.permissions.canQuickAddItem || false,
        canAddAccount: cashierInfo.permissions.canAddAccount || false,
        canPosRefund: cashierInfo.permissions.canPosRefund || false, // ✅ YENİ: POS İade yetkisi
        canClearCart: cashierInfo.permissions.canClearCart ?? true, // ✅ YENİ: Sepeti Temizle yetkisi (varsayılan AÇIK)
        canViewEndOfDay: cashierInfo.permissions.canViewEndOfDay || false, // ✅ YENİ: Gün Sonu yazdırma yetkisi
      };
      console.log('🔐 [QuickSaleScreen] Kasiyer yetkileri currentUserPermissions\'a set edildi:', cashierPermissions);
      setCurrentUserPermissions(cashierPermissions);
    }

    // Kasiyer giriş yaptıktan sonra ön ödemeli mod aktifse kart arama modalını aç
    if (prepaidSystemEnabled && !prepaidCard) {
      setTimeout(() => {
        setIsPrepaidCardSearchOpen(true);
      }, LAYOUT.TIMEOUTS.MODAL_DELAY);
    }

    // ✅ YENİ: Kasiyer giriş yapmadan ürün eklemeye çalıştıysa, şimdi ekle
    if (pendingProductForCashierLogin) {
      console.log('🛒 [QuickSaleScreen] Bekleyen ürün kontrol ediliyor:', pendingProductForCashierLogin.name);

      // addToCart fonksiyonunu burada çağırmak için bir timeout kullan
      // çünkü state güncellemesi tamamlanmamış olabilir
      setTimeout(() => {
        // ✅ Menülü ürün kontrolü
        const inventoryItem = sqlInventoryItems.find((item: InventoryItem) => item.id === pendingProductForCashierLogin.id);
        const hasMenu = inventoryItem?.hasMenu || pendingProductForCashierLogin.hasMenu || false;

        if (hasMenu) {
          console.log('🍽️ [KASİYER GİRİŞ] Bekleyen ürün menülü, opsiyon modalı açılıyor...');
          // Menülü ürün - opsiyon modalı aç
          setPendingMenuProduct({
            productId: pendingProductForCashierLogin.id,
            productName: pendingProductForCashierLogin.name,
            productPrice: pendingProductForCashierLogin.price || pendingProductForCashierLogin.salePrice || 0,
            productVatRate: pendingProductForCashierLogin.vatRate || 0,
            productVatStatus: inventoryItem?.vatStatus || pendingProductForCashierLogin.vatStatus || 'excluded',
            productUnit: pendingProductForCashierLogin.unit || 'adet',
            itemCanBeNegative: inventoryItem?.canBeNegative === true,
            itemStockQuantity: inventoryItem?.quantity ?? 0,
            hasRecipe: inventoryItem?.hasRecipe || pendingProductForCashierLogin.hasRecipe || false,
            hasMenu: true,
          });
          setIsMenuOptionModalOpen(true);
        } else {
          console.log('🛒 [KASİYER GİRİŞ] Bekleyen ürün sepete ekleniyor...');
          // Normal ürün - direkt sepete ekle
          addToCart(pendingProductForCashierLogin, pendingQuantityForCashierLogin);
          focusSearchBar();
        }

        // Bekleyen ürünü temizle
        setPendingProductForCashierLogin(null);
        setPendingQuantityForCashierLogin(1);
      }, 100);
    }
  }, [pendingProductForCashierLogin, pendingQuantityForCashierLogin, addToCart, focusSearchBar, sqlInventoryItems, prepaidSystemEnabled, prepaidCard, setCurrentCashier, setIsCashierLoginModalOpen, setCurrentUserPermissions, setIsPrepaidCardSearchOpen, setPendingMenuProduct, setIsMenuOptionModalOpen, setPendingProductForCashierLogin, setPendingQuantityForCashierLogin]);

  /**
   * ✅ Kasiyer çıkış yap
   */
  const handleCashierLogout = useCallback(() => {
    if (currentCashier) {
      console.log('🚪 [QuickSaleScreen] Kasiyer çıkış yaptı:', currentCashier.cashierName);
    }
    setCurrentCashier(null);

    // ✅ YENİ: Kasiyer çıkış yaptığında yetkileri sıfırla
    // Kasiyer sistemi aktifse, yeni kasiyer giriş yapana kadar tüm yetkiler kapalı olmalı
    if (useCashierSystem) {
      console.log('🔐 [QuickSaleScreen] Kasiyer çıkış yaptı, yetkiler sıfırlanıyor...');
      setCurrentUserPermissions({
        changePrice: false,
        applyDiscount: false,
        cancel: false,
        viewReports: false,
        warehouseOperations: false,
        viewAccountBalance: false,
        listAccounts: false,
        canQuickAddItem: false,
        canAddAccount: false,
      });
    }

    if (prepaidSystemEnabled) {
      setPrepaidCard(null);
      setSelectedPrepaidWalletId(null);
      setIsPrepaidCardSearchOpen(false);
    }
  }, [currentCashier, useCashierSystem, prepaidSystemEnabled, setCurrentCashier, setCurrentUserPermissions, setPrepaidCard, setSelectedPrepaidWalletId, setIsPrepaidCardSearchOpen]);

  return {
    handleOpenCashierLogin,
    handleCashierLogin,
    handleCashierLogout,
  };
};

export default useCashierAuth;
